import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Trash2, Layers, Check, Sparkles, Pencil, FolderHeart } from "lucide-react";
import AutocompleteInput from "@/components/AutocompleteInput";

const FONT_OPTIONS = [
  { id: "display", label: "Display (Barlow)", className: "font-display uppercase" },
  { id: "mono", label: "Mono Tabular", className: "font-mono-tc uppercase" },
  { id: "serif", label: "Serif", className: "uppercase", style: { fontFamily: "Georgia, serif" } },
  { id: "sans", label: "Sans", className: "uppercase", style: { fontFamily: "Inter, system-ui, sans-serif" } },
];

const TRANSITION_OPTIONS = [
  { id: "fade", label: "Fade", hint: "Soft black fade between clips" },
  { id: "crossfade", label: "Crossfade", hint: "Smooth blend, best for highlight reels" },
  { id: "cut", label: "Hard Cut", hint: "Snappy, broadcast style" },
];

const PRESET_COLORS = ["#f97316", "#dc2626", "#16a34a", "#0ea5e9", "#a855f7", "#eab308", "#f5f5f4", "#000000"];

export default function ClientTemplates() {
  const [templates, setTemplates] = useState([]);
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // template doc or {} for new

  const refresh = async () => {
    setLoading(true);
    try {
      const [t, g] = await Promise.all([
        api.get("/client-reel-templates"),
        api.get("/galleries"),
      ]);
      setTemplates(t.data.templates || []);
      setGalleries(g.data.galleries || []);
    } catch (e) {
      toast.error("Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const del = async (id, name) => {
    if (!window.confirm(`Delete template "${name}"?`)) return;
    try {
      await api.delete(`/client-reel-templates/${id}`);
      toast.success("Template deleted");
      refresh();
    } catch (e) { toast.error("Failed to delete"); }
  };

  return (
    <div className="px-6 md:px-12 py-10 max-w-[1300px]" data-testid="client-templates-page">
      <div className="flex items-end justify-between mb-8 gap-6">
        <div className="border-l-4 border-orange-600 pl-5">
          <div className="label-tiny mb-2">Client-Facing Reel Templates</div>
          <h1 className="font-display uppercase text-5xl font-bold leading-[0.95]">Templates</h1>
          <p className="text-stone-400 mt-3 max-w-xl">
            Branded reel presets your clients pick from when they build their own highlight reel. Assign each template to specific events so a client only sees the templates designed for their gallery.
          </p>
        </div>
        <button onClick={() => setEditing({})} className="btn-primary inline-flex items-center gap-2" data-testid="new-template-btn">
          <Plus size={14} /> New Template
        </button>
      </div>

      {loading ? (
        <div className="text-stone-500 py-12 text-center">Loading…</div>
      ) : templates.length === 0 ? (
        <div className="border border-dashed border-stone-800 p-16 text-center" data-testid="templates-empty">
          <Layers size={32} className="mx-auto text-stone-600 mb-3" />
          <p className="text-stone-400">No templates yet.</p>
          <p className="text-stone-500 text-sm mt-2">Build your first branded reel preset for your clients.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="templates-grid">
          {templates.map((t) => (
            <TemplateCard
              key={t.id}
              tpl={t}
              galleries={galleries}
              onEdit={() => setEditing(t)}
              onDelete={() => del(t.id, t.name)}
            />
          ))}
        </div>
      )}

      {editing && (
        <TemplateEditor
          tpl={editing}
          galleries={galleries}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}

function TemplateCard({ tpl, galleries, onEdit, onDelete }) {
  const galleryNames = (tpl.gallery_ids || [])
    .map((id) => galleries.find((g) => g.id === id)?.name)
    .filter(Boolean);
  const fontDef = FONT_OPTIONS.find((f) => f.id === tpl.font_family) || FONT_OPTIONS[0];

  return (
    <div className="border border-stone-800 hover:border-orange-700 transition-colors group flex flex-col" data-testid={`template-card-${tpl.id}`}>
      <TemplatePreview tpl={tpl} fontDef={fontDef} />
      <div className="p-4 flex-1 flex flex-col">
        <div className="font-display uppercase font-bold text-lg leading-tight">{tpl.name}</div>
        {tpl.description && <div className="text-xs text-stone-500 mt-1 line-clamp-2">{tpl.description}</div>}
        <div className="flex flex-wrap gap-1.5 mt-3 text-[10px] uppercase tracking-widest text-stone-500">
          <span className="px-1.5 py-0.5 border border-stone-800">{tpl.transition_style || "fade"}</span>
          {tpl.sponsor_text && <span className="px-1.5 py-0.5 border border-stone-800">sponsor line</span>}
          {tpl.music_url && <span className="px-1.5 py-0.5 border border-stone-800">music</span>}
          {tpl.logo_s3_key && <span className="px-1.5 py-0.5 border border-stone-800">logo</span>}
        </div>
        <div className="mt-3 text-[11px] text-stone-400 inline-flex items-start gap-1">
          <FolderHeart size={11} className="mt-0.5 shrink-0" />
          <span>
            {galleryNames.length === 0
              ? <span className="text-amber-500">No galleries assigned — clients can't see this yet.</span>
              : galleryNames.join(" · ")}
          </span>
        </div>
        <div className="flex items-center gap-1 mt-auto pt-3 border-t border-stone-800">
          <button onClick={onEdit} className="btn-secondary !py-1.5 !px-2 inline-flex items-center gap-1.5 text-xs" data-testid={`template-edit-${tpl.id}`}>
            <Pencil size={11} /> Edit
          </button>
          <button onClick={onDelete} className="btn-secondary !py-1.5 !px-2 ml-auto hover:!text-rose-500" data-testid={`template-delete-${tpl.id}`}>
            <Trash2 size={11} />
          </button>
        </div>
      </div>
    </div>
  );
}

function TemplatePreview({ tpl, fontDef }) {
  const accent = tpl.accent_color || "#f97316";
  return (
    <div className="aspect-video bg-stone-950 relative overflow-hidden border-b border-stone-800" data-testid="template-preview">
      {/* faux frame */}
      <div className="absolute inset-0 bg-gradient-to-br from-stone-900 via-stone-950 to-black" />
      <div className="absolute inset-0 opacity-30" style={{
        background: `radial-gradient(circle at 20% 30%, ${accent}55, transparent 50%)`,
      }} />
      <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: accent }} />

      <div className="relative h-full flex flex-col items-center justify-center px-6 text-center">
        <div className={`text-stone-100 leading-tight ${fontDef.className}`} style={{ ...fontDef.style, fontSize: "26px", fontWeight: 700 }}>
          {tpl.intro_card_text || "Your Event Title"}
        </div>
        {(tpl.intro_subtext || tpl.sponsor_text) && (
          <div className="mt-1.5 text-[11px] uppercase tracking-[0.3em]" style={{ color: accent }}>
            {tpl.intro_subtext || tpl.sponsor_text}
          </div>
        )}
      </div>

      {/* lower-third hint */}
      <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2">
        <div className="w-1 h-6" style={{ background: accent }} />
        <div className={`text-[11px] uppercase tracking-widest text-stone-200 ${fontDef.className}`} style={fontDef.style}>
          Rider · Horse
        </div>
      </div>
    </div>
  );
}

function TemplateEditor({ tpl, galleries, onClose, onSaved }) {
  const isNew = !tpl.id;
  const [name, setName] = useState(tpl.name || "");
  const [description, setDescription] = useState(tpl.description || "");
  const [intro, setIntro] = useState(tpl.intro_card_text || "");
  const [introSub, setIntroSub] = useState(tpl.intro_subtext || "");
  const [outro, setOutro] = useState(tpl.outro_card_text || "");
  const [outroSub, setOutroSub] = useState(tpl.outro_subtext || "");
  const [transition, setTransition] = useState(tpl.transition_style || "crossfade");
  const [accent, setAccent] = useState(tpl.accent_color || "#f97316");
  const [font, setFont] = useState(tpl.font_family || "display");
  const [sponsor, setSponsor] = useState(tpl.sponsor_text || "");
  const [music, setMusic] = useState(tpl.music_url || "");
  const [galleryIds, setGalleryIds] = useState(new Set(tpl.gallery_ids || []));
  const [busy, setBusy] = useState(false);

  const fontDef = useMemo(() => FONT_OPTIONS.find((f) => f.id === font) || FONT_OPTIONS[0], [font]);
  const livePreview = {
    name, intro_card_text: intro, intro_subtext: introSub,
    accent_color: accent, font_family: font, sponsor_text: sponsor,
    transition_style: transition, music_url: music,
  };

  const toggleGallery = (id) => {
    setGalleryIds((p) => {
      const n = new Set(p);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    const body = {
      name: name.trim(),
      description: description.trim(),
      intro_card_text: intro.trim(),
      intro_subtext: introSub.trim(),
      outro_card_text: outro.trim(),
      outro_subtext: outroSub.trim(),
      transition_style: transition,
      accent_color: accent,
      font_family: font,
      sponsor_text: sponsor.trim(),
      music_url: music.trim(),
      gallery_ids: Array.from(galleryIds),
    };
    try {
      if (isNew) await api.post("/client-reel-templates", body);
      else await api.patch(`/client-reel-templates/${tpl.id}`, body);
      toast.success(isNew ? "Template created" : "Template saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center px-4 py-8 overflow-y-auto" onClick={onClose}>
      <div className="bg-stone-950 border border-stone-800 w-full max-w-5xl" onClick={(e) => e.stopPropagation()} data-testid="template-editor">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-800">
          <div>
            <div className="label-tiny mb-1">{isNew ? "Create" : "Edit"} Template</div>
            <h2 className="font-display uppercase text-2xl font-bold">{name || "Untitled Template"}</h2>
          </div>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-200" data-testid="editor-close"><X size={20} /></button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-0">
          <form onSubmit={submit} className="p-6 space-y-5 border-r border-stone-800">
            <Section title="Identity">
              <Field label="Template name" required>
                <AutocompleteInput field="template_name" value={name} onChange={setName} className="field-input w-full" data-testid="tpl-name" autoFocus />
              </Field>
              <Field label="Description">
                <AutocompleteInput as="textarea" field="description" value={description} onChange={setDescription} rows={2} className="field-input w-full resize-none" data-testid="tpl-description" />
              </Field>
            </Section>

            <Section title="Look & Feel">
              <Field label="Accent color">
                <div className="flex items-center gap-2 flex-wrap">
                  <input type="color" value={accent} onChange={(e) => setAccent(e.target.value)} className="w-10 h-10 cursor-pointer bg-transparent border border-stone-700" data-testid="tpl-accent" />
                  <input type="text" value={accent} onChange={(e) => setAccent(e.target.value)} className="field-input font-mono-tc text-sm flex-1 min-w-[120px]" />
                  <div className="flex gap-1">
                    {PRESET_COLORS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setAccent(c)}
                        className={`w-6 h-6 border ${accent.toLowerCase() === c.toLowerCase() ? "border-stone-100 border-2" : "border-stone-700"}`}
                        style={{ background: c }}
                        data-testid={`tpl-color-${c}`}
                      />
                    ))}
                  </div>
                </div>
              </Field>
              <Field label="Title font">
                <div className="grid grid-cols-2 gap-2">
                  {FONT_OPTIONS.map((f) => (
                    <button
                      type="button"
                      key={f.id}
                      onClick={() => setFont(f.id)}
                      className={`text-left px-3 py-2 border text-sm transition-colors ${font === f.id ? "border-orange-600 bg-orange-600/5" : "border-stone-800 hover:border-stone-700"}`}
                      data-testid={`tpl-font-${f.id}`}
                    >
                      <span className={f.className} style={f.style}>{f.label}</span>
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="Clip transitions">
                <div className="grid grid-cols-3 gap-2">
                  {TRANSITION_OPTIONS.map((t) => (
                    <button
                      type="button"
                      key={t.id}
                      onClick={() => setTransition(t.id)}
                      title={t.hint}
                      className={`px-3 py-2 border text-xs transition-colors ${transition === t.id ? "border-orange-600 bg-orange-600/5 text-stone-100" : "border-stone-800 hover:border-stone-700 text-stone-400"}`}
                      data-testid={`tpl-transition-${t.id}`}
                    >
                      <div className="font-display uppercase font-semibold">{t.label}</div>
                      <div className="text-[10px] text-stone-500 mt-0.5">{t.hint}</div>
                    </button>
                  ))}
                </div>
              </Field>
            </Section>

            <Section title="Intro Card">
              <Field label="Main title">
                <AutocompleteInput field="intro_text" value={intro} onChange={setIntro} placeholder="ROYAL CROWN 2026" className="field-input w-full" data-testid="tpl-intro" />
              </Field>
              <Field label="Subtitle / event date">
                <AutocompleteInput field="intro_subtext" value={introSub} onChange={setIntroSub} placeholder="Calgary · June 18" className="field-input w-full" data-testid="tpl-intro-sub" />
              </Field>
            </Section>

            <Section title="Outro Card">
              <Field label="Closing line">
                <AutocompleteInput field="outro_text" value={outro} onChange={setOutro} placeholder="Thanks for riding with us" className="field-input w-full" data-testid="tpl-outro" />
              </Field>
              <Field label="Tagline / sign-off">
                <AutocompleteInput field="outro_subtext" value={outroSub} onChange={setOutroSub} placeholder="@rodeocutstudio" className="field-input w-full" data-testid="tpl-outro-sub" />
              </Field>
            </Section>

            <Section title="Sponsor & Music">
              <Field label="Sponsor line (shows under intro)">
                <AutocompleteInput field="sponsor_text" value={sponsor} onChange={setSponsor} placeholder="Presented by Mountain Vet" className="field-input w-full" data-testid="tpl-sponsor" />
              </Field>
              <Field label="Background music URL (optional)">
                <AutocompleteInput field="url" value={music} onChange={setMusic} placeholder="https://… mp3" className="field-input w-full" data-testid="tpl-music" />
              </Field>
            </Section>

            <Section title="Galleries">
              <p className="text-xs text-stone-500 mb-2">Pick which events can use this template. Clients only see templates assigned to their gallery.</p>
              {galleries.length === 0 ? (
                <p className="text-xs text-stone-500">No galleries yet — create one first.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-44 overflow-y-auto" data-testid="tpl-gallery-grid">
                  {galleries.map((g) => {
                    const on = galleryIds.has(g.id);
                    return (
                      <button
                        type="button"
                        key={g.id}
                        onClick={() => toggleGallery(g.id)}
                        className={`text-left p-2 border text-xs transition-colors ${on ? "border-orange-600 bg-orange-600/5" : "border-stone-800 hover:border-stone-700"}`}
                        data-testid={`tpl-gallery-${g.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className={`w-3.5 h-3.5 border ${on ? "border-orange-500 bg-orange-600/30" : "border-stone-600"} inline-flex items-center justify-center shrink-0`}>
                            {on && <Check size={9} className="text-orange-400" />}
                          </span>
                          <span className="truncate">{g.name}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </Section>

            <div className="flex gap-2 pt-2 border-t border-stone-800">
              <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
              <button type="submit" disabled={busy || !name.trim()} className="btn-primary flex-1 inline-flex items-center justify-center gap-1.5" data-testid="tpl-save">
                <Sparkles size={12} /> {busy ? "Saving…" : isNew ? "Create Template" : "Save Changes"}
              </button>
            </div>
          </form>

          <div className="p-6 bg-stone-925 sticky top-0 self-start" style={{ background: "rgba(28,25,23,0.4)" }}>
            <div className="label-tiny mb-3">Live preview</div>
            <TemplatePreview tpl={livePreview} fontDef={fontDef} />
            <div className="mt-4 grid grid-cols-2 gap-2 text-[11px]">
              <PreviewSpec label="Transition" value={transition} />
              <PreviewSpec label="Font" value={fontDef.label.split(" ")[0]} />
              <PreviewSpec label="Accent" value={accent} />
              <PreviewSpec label="Galleries" value={String(galleryIds.size)} />
            </div>
            {outro && (
              <div className="mt-4">
                <div className="label-tiny mb-2">Outro card</div>
                <TemplatePreview
                  tpl={{ ...livePreview, intro_card_text: outro, intro_subtext: outroSub }}
                  fontDef={fontDef}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="label-tiny mb-2">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <div className="text-[11px] text-stone-500 mb-1">{label}{required && <span className="text-orange-500"> *</span>}</div>
      {children}
    </div>
  );
}

function PreviewSpec({ label, value }) {
  return (
    <div className="border border-stone-800 p-2">
      <div className="text-[9px] uppercase tracking-widest text-stone-500">{label}</div>
      <div className="font-mono-tc text-stone-200 truncate text-xs mt-0.5">{value}</div>
    </div>
  );
}
